import { Alert, Button, Form } from "antd"
import { type Store } from "antd/lib/form/interface"
import { type Moment } from "moment"
import React from "react"
import { useTranslation } from "react-i18next"
import { Redirect, useLocation } from "react-router-dom"
import { FullScreenLoader } from "~Components/FullScreenLoader"
import { DateOfBirthFormItem } from "~Components/forms/employeeForm/dateOfBirth.formItem"
import { AuthLayout } from "~Features/authentication/auth.layout"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { useSaveDateOfBirth } from "~Shared/hooks/dateOfBirth.hooks"
import { useActiveCompanyQuery } from "~Shared/queries/company.queries"
import { useEmployee, useLoggedInEmployeeProfile } from "~Shared/queries/employee.queries"
import { type LoginRedirectState } from "~Shared/router/PrivateRoute"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { isDateOfBirthRequired } from "~Shared/utils/employee.utils"
import { generatePath } from "~Shared/utils/routing.utils"

export const DateOfBirthPage = () => {
    const { t } = useTranslation()
    const { logout, userId, isFinwayAdmin } = useAuth()
    const employee = useLoggedInEmployeeProfile()
    const { isLoading: isProfileLoading } = useEmployee(userId)
    const { data: company, isLoading: isCompanyLoading } = useActiveCompanyQuery()
    const { state } = useLocation<LoginRedirectState | undefined>()
    const { saveDateOfBirth, isSaving } = useSaveDateOfBirth()
    const [formInstance] = Form.useForm()

    const handleSubmit = async ({ dateOfBirth }: Store) => {
        if (!employee) {
            return
        }

        try {
            await saveDateOfBirth(employee, dateOfBirth as Moment)
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:date_of_birth_saved.title"), t("notification:date_of_birth_saved.message"))
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.edit.title"))
        }
    }

    /** Guarded by `userId` because a query disabled for the want of one reports `loading` for good */
    const isProfileSettled = !userId || !isProfileLoading
    if (!isProfileSettled || isCompanyLoading) {
        return <FullScreenLoader />
    }

    if (!isDateOfBirthRequired({ employee, isCardProgramEnabled: company?.cardsEnabled, isFinwayAdmin })) {
        return <Redirect to={state?.from ?? generatePath(RoutePathEnum.ROOT)} />
    }

    return (
        <AuthLayout>
            <h2 className="text-center mb-8">{t("label:enter_date_of_birth")}</h2>
            <p className="text-center mb-24">{t("info:date_of_birth_required")}</p>

            <Form form={formInstance} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                <DateOfBirthFormItem inputClassName="w-full border-1 border-gray-500 rounded-lg outline-login min-h-40" />
            </Form>

            <Alert type="success" showIcon className="mb-24" message={t("info:date_of_birth_private")} />

            <Button size="large" className="w-full rounded-lg font-medium bg-login-button" type="primary" loading={isSaving} onClick={formInstance.submit}>
                {t("action:save_and_continue_to_finway")}
            </Button>

            <Button type="link" className="w-full mt-8" onClick={logout}>
                {t("label:logout")}
            </Button>
        </AuthLayout>
    )
}
